import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CompanyCenterState, CompanyItem, SponsorListItem } from './types';

import {
  createCompany,
  fetchAllCompany,
  fetchAllCompanyEntities,
  fetchAllCompanyTypes,
  fetchAllOwnershipTypes,
  fetchHedgeStandards,
  fetchSponsorList,
  updateCompany,
} from './thunks';
import { OptionType, SingleAutocompleteResponse } from '@/components/AutoComplete';
import { CompanyCenterTableColumn } from '@/types/company';

// LocalStorage keys
const STORAGE_KEYS = {
  COMPANY_LIST: 'companyCenter_companyList',
  COMPANY_CENTER_LIST: 'companyCenter_companyCenterList',
  SPONSOR_LIST: 'companyCenter_sponsorList',
};

// Helper functions for localStorage
const loadFromStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (error) {
    console.error(`Error loading ${key} from localStorage:`, error);
    return defaultValue;
  }
};

const saveToStorage = <T>(key: string, data: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

const initialState: CompanyCenterState = {
  companyList: loadFromStorage<CompanyItem[]>(STORAGE_KEYS.COMPANY_LIST, []),
  companyListLoading: false,
  companyListError: null,

  companyTypes: [],
  companyTypesLoading: false,
  companyTypesError: null,

  ownershipTypes: [],
  ownershipTypesLoading: false,
  ownershipTypesError: null,

  companyCenterList: loadFromStorage<CompanyCenterTableColumn[]>(
    STORAGE_KEYS.COMPANY_CENTER_LIST,
    [],
  ),
  companyCenterListLoading: false,
  companyCenterListError: null,

  sponsorList: loadFromStorage<SponsorListItem[]>(
    STORAGE_KEYS.SPONSOR_LIST,
    [],
  ),
  sponsorListLoading: false,
  sponsorListError: null,

  hedgeStandards: [],
  hedgeStandardsLoading: false,
  hedgeStandardsError: null,
};

const CompanyCenterSlice = createSlice({
  name: 'companyCenter',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // All Company
      .addCase(fetchAllCompany.pending, (state) => {
        state.companyListLoading = true;
        state.companyListError = null;
      })
      .addCase(
        fetchAllCompany.fulfilled,
        (state, action: PayloadAction<CompanyItem[]>) => {
          state.companyListLoading = false;
          state.companyList = action.payload;
          saveToStorage(STORAGE_KEYS.COMPANY_LIST, action.payload);
        },
      )
      .addCase(fetchAllCompany.rejected, (state, action) => {
        state.companyListLoading = false;
        state.companyListError = action.payload as string;
      })
      // All Company Types
      .addCase(fetchAllCompanyTypes.pending, (state) => {
        state.companyTypesLoading = true;
        state.companyTypesError = null;
      })
      .addCase(
        fetchAllCompanyTypes.fulfilled,
        (state, action: PayloadAction<SingleAutocompleteResponse[]>) => {
          state.companyTypesLoading = false;
          state.companyTypes = action.payload;
        },
      )
      .addCase(fetchAllCompanyTypes.rejected, (state, action) => {
        state.companyTypesLoading = false;
        state.companyTypesError = action.payload as string;
      })
      // Ownership Types
      .addCase(fetchAllOwnershipTypes.pending, (state) => {
        state.ownershipTypesLoading = true;
        state.ownershipTypesError = null;
      })
      .addCase(
        fetchAllOwnershipTypes.fulfilled,
        (state, action: PayloadAction<SingleAutocompleteResponse[]>) => {
          state.ownershipTypesLoading = false;
          state.ownershipTypes = action.payload;
        },
      )
      .addCase(fetchAllOwnershipTypes.rejected, (state, action) => {
        state.ownershipTypesLoading = false;
        state.ownershipTypesError = action.payload as string;
      })
      // Create Company
      .addCase(createCompany.pending, (state) => {
        state.companyListLoading = true;
        state.companyListError = null;
      })
      .addCase(createCompany.fulfilled, (state) => {
        state.companyListLoading = false;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.companyListLoading = false;
        state.companyListError = action.payload as string;
      })
      // Update Company
      .addCase(updateCompany.pending, (state) => {
        state.companyListLoading = true;
        state.companyListError = null;
      })
      .addCase(updateCompany.fulfilled, (state) => {
        state.companyListLoading = false;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.companyListLoading = false;
        state.companyListError = action.payload as string;
      })
      // Get All Company Entities
      .addCase(fetchAllCompanyEntities.pending, (state) => {
        state.companyCenterListLoading = true;
        state.companyCenterListError = null;
      })
      .addCase(
        fetchAllCompanyEntities.fulfilled,
        (state, action: PayloadAction<CompanyCenterTableColumn[]>) => {
          state.companyCenterListLoading = false;
          state.companyCenterList = action.payload;
          saveToStorage(STORAGE_KEYS.COMPANY_CENTER_LIST, action.payload);
        },
      )
      .addCase(fetchAllCompanyEntities.rejected, (state, action) => {
        state.companyCenterListLoading = false;
        state.companyCenterListError = action.payload as string;
      })
      // Fetch Sponsor List
      .addCase(fetchSponsorList.pending, (state) => {
        state.sponsorListLoading = true;
        state.sponsorListError = null;
      })
      .addCase(
        fetchSponsorList.fulfilled,
        (state, action: PayloadAction<SponsorListItem[]>) => {
          state.sponsorListLoading = false;
          state.sponsorList = action.payload;
          saveToStorage(STORAGE_KEYS.SPONSOR_LIST, action.payload);
        },
      )
      .addCase(fetchSponsorList.rejected, (state, action) => {
        state.sponsorListLoading = false;
        state.sponsorListError = action.payload as string;
      })
      // Hedge Standards
      .addCase(fetchHedgeStandards.pending, (state) => {
        state.hedgeStandardsLoading = true;
        state.hedgeStandardsError = null;
      })
      .addCase(
        fetchHedgeStandards.fulfilled,
        (state, action: PayloadAction<OptionType[]>) => {
          state.hedgeStandardsLoading = false;
          state.hedgeStandards = action.payload;
        },
      )
      .addCase(fetchHedgeStandards.rejected, (state, action) => {
        state.hedgeStandardsLoading = false;
        state.hedgeStandardsError = action.payload as string;
      });
  },
});

export default CompanyCenterSlice.reducer;
